// B5 Candidatos: todos los de todos los clientes (tablero o lista con semáforo, filtro por cliente y
// vacante) y la bolsa de candidatos sin cliente. Misma ficha que ve el cliente, más la conversación.
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { ArrowLeft, Columns3, List as ListIcon, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useGetOne, useNotify, useRedirect, useRefresh, useUpdate } from "ra-core";
import { Link, useParams } from "react-router";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  COLUMNAS,
  ETIQUETA_COLUMNA,
  ETIQUETA_ETAPA,
  ETAPAS,
  columnaDe,
  etapaDeColumna,
  fechaHora,
  normalizarTelefono,
} from "@/reclutamiento/logica";
import type { Candidato, Columna, Etapa, Vacante } from "@/reclutamiento/tipos";
import {
  BotonesContacto,
  Conversacion,
  Encabezado,
  IconosCriterios,
  MarcaSemaforo,
  Pildora,
  PildoraVeredicto,
  Seccion,
  VisorCv,
} from "../comun";
import { useCandidatos, useClientes, useVacantes } from "../datos";

const selectCls =
  "h-9 rounded-md border bg-background px-2 text-sm focus-visible:outline-2 focus-visible:outline-[var(--acento)]";

// ---------------------------------------------------------------- lista / tablero

export const CandidatosLista = () => {
  const { data: candidatos, isPending } = useCandidatos();
  const { data: clientes } = useClientes();
  const { data: vacantes } = useVacantes();
  const [pestana, setPestana] = useState<"clientes" | "bolsa">(() =>
    new URLSearchParams(window.location.hash.split("?")[1]).get("bolsa") ? "bolsa" : "clientes",
  );
  const [vista, setVista] = useState<"tablero" | "lista">("lista");
  const [cliente, setCliente] = useState("");
  const [vacante, setVacante] = useState("");
  const [veredicto, setVeredicto] = useState("");
  const [q, setQ] = useState("");

  const nombreCliente = (id: string | null) => clientes.find((c) => c.id === id)?.nombre ?? "—";
  const vac = (id: string | null) => vacantes.find((v) => v.id === id);

  const deClientes = candidatos.filter((c) => c.cliente_id);
  const bolsa = candidatos.filter((c) => !c.cliente_id);

  const filtrados = useMemo(
    () =>
      deClientes.filter(
        (c) =>
          (!cliente || c.cliente_id === cliente) &&
          (!vacante || c.vacante_id === vacante) &&
          (!veredicto || c.veredicto === veredicto) &&
          (!q || `${c.nombre} ${c.ciudad} ${c.motivo ?? ""}`.toLowerCase().includes(q.toLowerCase())),
      ),
    [deClientes, cliente, vacante, veredicto, q],
  );
  const vacantesDelCliente = vacantes.filter((v) => !cliente || v.cliente_id === cliente);

  return (
    <div>
      <Encabezado
        ceja="Todos los clientes"
        titulo="Candidatos"
        sub="Para revisar y dar soporte. Es la misma ficha que ve el cliente, más la conversación con el bot."
        acciones={
          <Button asChild size="sm">
            <Link to="/candidatos/create">
              <Plus className="size-4" /> Alta manual
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b" role="tablist">
        {(
          [
            ["clientes", `De clientes (${deClientes.length})`],
            ["bolsa", `Bolsa de candidatos (${bolsa.length})`],
          ] as const
        ).map(([k, t]) => (
          <button
            key={k}
            role="tab"
            aria-selected={pestana === k}
            onClick={() => setPestana(k)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
              pestana === k ? "border-[var(--acento)] text-foreground" : "border-transparent text-muted-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {pestana === "bolsa" ? (
        <Bolsa candidatos={bolsa} vacantes={vacantes} nombreCliente={nombreCliente} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input
              className={cn(selectCls, "w-48")}
              placeholder="Buscar nombre o ciudad"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Buscar"
            />
            <select className={selectCls} value={cliente} onChange={(e) => { setCliente(e.target.value); setVacante(""); }} aria-label="Cliente">
              <option value="">Todos los clientes</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            <select className={selectCls} value={vacante} onChange={(e) => setVacante(e.target.value)} aria-label="Vacante">
              <option value="">Todas las vacantes</option>
              {vacantesDelCliente.map((v) => (
                <option key={v.id} value={v.id}>{v.puesto}</option>
              ))}
            </select>
            <select className={selectCls} value={veredicto} onChange={(e) => setVeredicto(e.target.value)} aria-label="Veredicto">
              <option value="">Todos los veredictos</option>
              <option value="cumple">Cumple</option>
              <option value="revisar">Revisar</option>
              <option value="descartado">Descartado</option>
            </select>
            <div className="ml-auto flex rounded-lg border p-0.5" role="group" aria-label="Vista">
              <Button variant={vista === "lista" ? "secondary" : "ghost"} size="sm" onClick={() => setVista("lista")} aria-pressed={vista === "lista"}>
                <ListIcon className="size-4" /> Lista
              </Button>
              <Button variant={vista === "tablero" ? "secondary" : "ghost"} size="sm" onClick={() => setVista("tablero")} aria-pressed={vista === "tablero"}>
                <Columns3 className="size-4" /> Tablero
              </Button>
            </div>
          </div>
          <Conteo lista={filtrados} />
          {isPending ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : vista === "lista" ? (
            <Tabla lista={filtrados} nombreCliente={nombreCliente} vac={vac} />
          ) : (
            <Tablero lista={filtrados} nombreCliente={nombreCliente} vac={vac} />
          )}
        </>
      )}
    </div>
  );
};

const Conteo = ({ lista }: { lista: Candidato[] }) => (
  <div className="mb-3 flex flex-wrap gap-2 text-sm">
    <Pildora tono="ok"><MarcaSemaforo tipo="cumple" size={13} /> {lista.filter((c) => c.veredicto === "cumple").length} cumplen</Pildora>
    <Pildora tono="revisar"><MarcaSemaforo tipo="revisar" size={13} /> {lista.filter((c) => c.veredicto === "revisar").length} a revisar</Pildora>
    <Pildora tono="bad"><MarcaSemaforo tipo="descartado" size={13} /> {lista.filter((c) => c.veredicto === "descartado").length} descartados</Pildora>
    <Pildora tono="neutro">{lista.filter((c) => c.etapa === "entrevistado" || c.etapa === "contratado").length} entrevistados · {lista.filter((c) => c.etapa === "contratado").length} contratados · {lista.filter((c) => c.etapa === "algun_dia").length} en algún día</Pildora>
  </div>
);

type PropsVista = {
  lista: Candidato[];
  nombreCliente: (id: string | null) => string;
  vac: (id: string | null) => Vacante | undefined;
};

const Tabla = ({ lista, nombreCliente, vac }: PropsVista) => {
  const redirect = useRedirect();
  if (!lista.length) return <p className="py-8 text-center text-sm text-muted-foreground">No hay candidatos con esos filtros.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Nombre</th>
            <th className="px-3 py-2">Veredicto</th>
            <th className="px-3 py-2">Criterios</th>
            <th className="px-3 py-2">Cliente · vacante</th>
            <th className="px-3 py-2">Ciudad</th>
            <th className="px-3 py-2">Etapa</th>
            <th className="px-3 py-2">Llegó</th>
            <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
          </tr>
        </thead>
        <tbody>
          {lista.map((c) => (
            <tr key={c.id} className="cursor-pointer border-t hover:bg-muted/30" onClick={() => redirect(`/candidatos/${c.id}/show`)}>
              <td className="px-3 py-2 font-medium">{c.nombre}</td>
              <td className="px-3 py-2">
                <PildoraVeredicto veredicto={c.veredicto} />
                {c.motivo ? <p className="mt-1 max-w-64 truncate text-xs text-muted-foreground" title={c.motivo}>{c.motivo}</p> : null}
              </td>
              <td className="px-3 py-2"><IconosCriterios criterios={c.criterios} /></td>
              <td className="px-3 py-2 text-xs">{nombreCliente(c.cliente_id)}<br /><span className="text-muted-foreground">{vac(c.vacante_id)?.puesto}</span></td>
              <td className="px-3 py-2">{c.ciudad}</td>
              <td className="px-3 py-2">{ETIQUETA_COLUMNA[columnaDe(c.veredicto, c.etapa)]}</td>
              <td className="num px-3 py-2">{fechaHora(c.recibido_en, c.ingresado_en)}</td>
              <td className="px-3 py-2"><BotonesContacto telefono={c.telefono} nombre={c.nombre} puesto={vac(c.vacante_id)?.puesto} empresa={nombreCliente(c.cliente_id)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Tablero = ({ lista, nombreCliente, vac }: PropsVista) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [local, setLocal] = useState<Record<string, Etapa>>({});
  const col = (c: Candidato): Columna => columnaDe(c.veredicto, local[c.id] ?? c.etapa);

  const soltar = (r: DropResult) => {
    if (!r.destination) return;
    const c = lista.find((x) => x.id === r.draggableId);
    const destino = r.destination.droppableId as Columna;
    if (!c || destino === col(c)) return;
    const etapa = etapaDeColumna(destino);
    setLocal((p) => ({ ...p, [c.id]: etapa }));
    update(
      "candidatos",
      { id: c.id, data: { etapa, etapa_cambiada_en: new Date().toISOString() }, previousData: c },
      {
        onSuccess: () => { notify(`${c.nombre} → ${ETIQUETA_COLUMNA[destino]}`, { type: "info" }); refresh(); },
        onError: (e: any) => { setLocal((p) => { const n = { ...p }; delete n[c.id]; return n; }); notify(e?.message ?? "No se pudo mover", { type: "error" }); },
      },
    );
  };

  return (
    <DragDropContext onDragEnd={soltar}>
      <div className="grid snap-x grid-flow-col auto-cols-[minmax(15rem,1fr)] gap-3 overflow-x-auto pb-3">
        {COLUMNAS.map((k) => {
          const items = lista.filter((c) => col(c) === k);
          return (
            <Droppable droppableId={k} key={k}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.droppableProps}
                  className={cn("snap-start rounded-xl bg-muted/50 p-2", snap.isDraggingOver && "ring-2 ring-[var(--acento)]", (k === "revisar" || k === "algun_dia" || k === "descartado") && "bg-muted/25")}
                >
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-sm font-semibold">{ETIQUETA_COLUMNA[k]}</span>
                    <span className="num rounded-full bg-background px-2 text-xs">{items.length}</span>
                  </div>
                  <div className="flex min-h-16 flex-col gap-2">
                    {items.map((c, i) => (
                      <Draggable draggableId={c.id} index={i} key={c.id}>
                        {(p, s) => (
                          <div
                            ref={p.innerRef}
                            {...(p.draggableProps as any)}
                            {...(p.dragHandleProps as any)}
                            onClick={() => redirect(`/candidatos/${c.id}/show`)}
                            className={cn("cursor-pointer rounded-lg border bg-card p-3 text-sm shadow-xs", s.isDragging && "rotate-1 shadow-lg")}
                          >
                            <div className="flex items-start gap-2">
                              <MarcaSemaforo tipo={c.veredicto} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium leading-tight">{c.nombre}</p>
                                <p className="truncate text-xs text-muted-foreground">{nombreCliente(c.cliente_id)} · {vac(c.vacante_id)?.puesto}</p>
                              </div>
                            </div>
                            {c.motivo ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.motivo}</p> : null}
                            <div className="mt-2 flex items-center justify-between">
                              <IconosCriterios criterios={c.criterios} />
                              <span className="num text-xs text-muted-foreground">{fechaHora(c.recibido_en, c.ingresado_en)}</span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {prov.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

// ---------------------------------------------------------------- bolsa

const Bolsa = ({
  candidatos,
  vacantes,
  nombreCliente,
}: {
  candidatos: Candidato[];
  vacantes: Vacante[];
  nombreCliente: (id: string | null) => string;
}) => {
  if (!candidatos.length)
    return <p className="py-8 text-center text-sm text-muted-foreground">La bolsa está vacía.</p>;
  return (
    <div>
      <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
        Personas que buscan trabajo y escribieron al anuncio de <b>empresas</b>. No se mezclan con los prospectos: quedan guardadas
        para ofrecerlas cuando abra una vacante que les quede.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {candidatos.map((c) => (
          <div key={c.id} className="rounded-xl border bg-card p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <Link to={`/candidatos/${c.id}/show`} className="font-semibold hover:underline">{c.nombre}</Link>
                <p className="text-xs text-muted-foreground">{c.ciudad} · llegó el {fechaHora(c.recibido_en, c.ingresado_en)} · anuncio {c.anuncio ?? "—"}</p>
              </div>
              <Pildora tono="acento">{c.interes ?? "Sin puesto"}</Pildora>
            </div>
            <p className="mt-2 text-sm">{c.resumen}</p>
            <div className="mt-3"><BotonesContacto telefono={c.telefono} nombre={c.nombre} puesto={c.interes} /></div>
            <AsignarVacante candidato={c} vacantes={vacantes} nombreCliente={nombreCliente} />
          </div>
        ))}
      </div>
    </div>
  );
};

const AsignarVacante = ({
  candidato,
  vacantes,
  nombreCliente,
}: {
  candidato: Candidato;
  vacantes: Vacante[];
  nombreCliente: (id: string | null) => string;
}) => {
  const [destino, setDestino] = useState("");
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const activas = vacantes.filter((v) => v.estado === "activa");
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
      <select className={selectCls} value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Vacante para ofrecerlo">
        <option value="">Ofrecer a una vacante…</option>
        {activas.map((v) => (
          <option key={v.id} value={v.id}>{nombreCliente(v.cliente_id)} · {v.puesto}</option>
        ))}
      </select>
      <Button
        size="sm"
        variant="outline"
        disabled={!destino || isPending}
        onClick={() => {
          const v = vacantes.find((x) => x.id === destino)!;
          update(
            "candidatos",
            {
              id: candidato.id,
              data: {
                cliente_id: v.cliente_id,
                vacante_id: v.id,
                veredicto: "revisar",
                motivo: "Viene de la bolsa: falta que el bot lo entreviste con los criterios de esta vacante",
                etapa: "nuevo",
              },
              previousData: candidato,
            },
            { onSuccess: () => { notify(`${candidato.nombre} pasó a «${v.puesto}»`, { type: "info" }); refresh(); } },
          );
        }}
      >
        Asignar
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------- ficha

export const CandidatoFicha = () => {
  const { id } = useParams();
  const { data: c, isPending, error, refetch } = useGetOne<Candidato & { id: string }>("candidatos", { id: id! });
  const { data: vacantes } = useVacantes();
  const { data: clientes } = useClientes();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  // El motivo y «Reintentar» los pone el aviso de arriba (ErroresDeConsulta); aquí no se dice «no existe».
  if (error) return <p className="text-sm text-muted-foreground">No se pudo abrir la ficha. <button type="button" className="underline" onClick={() => refetch()}>Reintentar</button></p>;
  if (isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (!c) return <p>No existe ese candidato.</p>;
  const v = vacantes.find((x) => x.id === c.vacante_id);
  const cli = clientes.find((x) => x.id === c.cliente_id);
  const columna = columnaDe(c.veredicto, c.etapa);
  const mover = (k: Columna) =>
    update(
      "candidatos",
      { id: c.id, data: { etapa: etapaDeColumna(k), etapa_cambiada_en: new Date().toISOString() }, previousData: c },
      { onSuccess: () => { notify(`Movido a ${ETIQUETA_COLUMNA[k]}`, { type: "info" }); refresh(); } },
    );

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-3 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm"><Link to="/candidatos"><ArrowLeft className="size-4" /> Candidatos</Link></Button>
        <Button asChild variant="outline" size="sm"><Link to={`/candidatos/${c.id}`}><Pencil className="size-4" /> Editar</Link></Button>
      </div>
      <Encabezado
        ceja={cli ? `${cli.nombre} · ${v?.puesto ?? ""}` : "Bolsa de candidatos"}
        titulo={c.nombre}
        sub={<>{c.ciudad} · llegó el {fechaHora(c.recibido_en, c.ingresado_en)} · anuncio {c.anuncio ?? "—"}{c.retirado_en ? " · ya no viene en la fuente" : ""}</>}
      />
      <div
        className={cn(
          "mb-4 flex items-center gap-3 rounded-xl p-4",
          c.veredicto === "cumple" ? "pill-ok" : c.veredicto === "revisar" ? "pill-revisar" : "pill-bad",
        )}
      >
        <MarcaSemaforo tipo={c.veredicto} size={40} />
        <div>
          <p className="text-lg font-semibold">{c.veredicto === "cumple" ? "Cumple" : c.veredicto === "revisar" ? "Revisar" : "Descartado"}</p>
          <p className="text-sm">{c.motivo ?? `Cumple los ${v?.criterios.length ?? ""} criterios de la vacante`}</p>
        </div>
      </div>
      <div className="mb-4"><BotonesContacto telefono={c.telefono} nombre={c.nombre} puesto={v?.puesto ?? c.interes} empresa={cli?.nombre} grande /></div>

      <div className="grid gap-4 md:grid-cols-2">
        <Seccion titulo="Etapa (la mueve el cliente)">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Mover de etapa">
            {COLUMNAS.map((k) => (
              <Button key={k} size="sm" variant={columna === k ? "default" : "outline"} aria-pressed={columna === k} onClick={() => mover(k)} disabled={!c.cliente_id}>
                {ETIQUETA_COLUMNA[k]}
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Nota del cliente: {c.nota_cliente || "sin nota"}</p>
          {c.etapa === "descartado" && (
            <p className="mt-1 text-xs text-muted-foreground">
              Lo descartó el cliente{c.motivo_cliente ? `: ${c.motivo_cliente}` : " (sin motivo)"}. Distinto del veredicto del filtro.
            </p>
          )}
        </Seccion>
        <Seccion titulo="Criterio por criterio">
          {v ? (
            <ul className="space-y-2">
              {v.criterios.map((cr) => {
                const r = c.criterios.find((x) => x.clave === cr.clave);
                return (
                  <li key={cr.clave} className="flex items-start gap-2 text-sm">
                    <MarcaSemaforo tipo={r?.estado ?? "falta"} />
                    <div className="min-w-0 flex-1">
                      <b className="block">{cr.texto}</b>
                      <span className="text-muted-foreground">{r?.dato ?? "Sin dato"}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{cr.efecto === "descarta" ? "Descarta" : cr.efecto === "marca" ? "Marca" : "Cumple"}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sin vacante asignada.</p>
          )}
        </Seccion>
        <Seccion titulo="Respuestas del cuestionario">
          <dl className="space-y-2 text-sm">
            {Object.entries(c.respuestas ?? {}).map(([p, r]) => (
              <div key={p}><dt className="text-muted-foreground">{p}</dt><dd className="font-medium">{r}</dd></div>
            ))}
          </dl>
        </Seccion>
        <Seccion titulo="Conversación con el bot · solo interna">
          <Conversacion mensajes={c.conversacion} nombre={c.nombre.split(" ")[0]} />
        </Seccion>
        <Seccion titulo="CV" className="md:col-span-2">
          <VisorCv cvUrl={c.cv_url} />
        </Seccion>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- alta y edición manual

const VEREDICTOS = [
  { id: "cumple", name: "Cumple" },
  { id: "revisar", name: "Revisar" },
  { id: "descartado", name: "Descartado" },
];
const ETAPAS_OPC = ETAPAS.map((e) => ({ id: e, name: ETIQUETA_ETAPA[e] }));

const Campos = () => {
  const { data: clientes } = useClientes();
  const { data: vacantes } = useVacantes();
  return (
    <>
      <TextInput source="nombre" label="Nombre" isRequired />
      <TextInput source="telefono" label="Teléfono (se guarda como +52…)" />
      <TextInput source="ciudad" label="Ciudad" />
      <SelectInput source="cliente_id" label="Cliente (vacío = bolsa)" choices={clientes.map((c) => ({ id: c.id, name: c.nombre }))} emptyText="Bolsa (sin cliente)" />
      <SelectInput source="vacante_id" label="Vacante" choices={vacantes.map((v) => ({ id: v.id, name: v.puesto }))} emptyText="Sin vacante" />
      <SelectInput source="veredicto" label="Veredicto" choices={VEREDICTOS} isRequired />
      <TextInput source="motivo" label="Motivo (obligatorio si no cumple)" multiline />
      <SelectInput source="etapa" label="Etapa" choices={ETAPAS_OPC} isRequired />
      <TextInput source="interes" label="Puesto que busca (bolsa)" />
      <TextInput source="resumen" label="Resumen (bolsa)" />
      <TextInput source="cv_url" label="CV (liga o ruta en el bucket «cvs»)" />
    </>
  );
};

const validar = (v: any): any => {
  const e: Record<string, string> = {};
  if (!v.nombre) e.nombre = "Falta el nombre";
  if (v.veredicto && v.veredicto !== "cumple" && !v.motivo) e.motivo = "Un «revisar» o «descartado» siempre lleva motivo";
  return e;
};

const transformar = (d: any) => {
  const ahora = new Date().toISOString();
  return {
    ...d,
    telefono: normalizarTelefono(d.telefono),
    cliente_id: d.cliente_id || null,
    vacante_id: d.vacante_id || null,
    origen_id: d.origen_id || `manual-${Date.now().toString(36)}`,
    criterios: d.criterios ?? [],
    respuestas: d.respuestas ?? {},
    recibido_en: d.recibido_en ?? ahora,
    ingresado_en: d.ingresado_en ?? ahora,
    actualizado_en: ahora,
  };
};

export const CandidatoAlta = () => (
  <Create transform={transformar} redirect="show" title="Alta manual de candidato">
    <SimpleForm validate={validar} defaultValues={{ veredicto: "revisar", etapa: "nuevo" }}>
      <Campos />
    </SimpleForm>
  </Create>
);

export const CandidatoEditar = () => (
  <Edit transform={transformar} redirect="show" title="Editar candidato" mutationMode="pessimistic">
    <SimpleForm validate={validar}>
      <Campos />
    </SimpleForm>
  </Edit>
);

export default {
  list: CandidatosLista,
  show: CandidatoFicha,
  create: CandidatoAlta,
  edit: CandidatoEditar,
  recordRepresentation: "nombre",
};
